import { useId, CSSProperties } from 'react'

interface MortarAndPestleProps {
  hasContent?: boolean
  grinding?: boolean
  contentColor?: string
  contentLevel?: number // 0-100
  onClick?: () => void
  disabled?: boolean
  className?: string
}

const MortarAndPestle = ({
  hasContent = false,
  grinding = false,
  contentColor = '#F5F5DC', // Default beige/cream color for tablets
  contentLevel = 50,
  onClick,
  disabled = false,
  className = ''
}: MortarAndPestleProps) => {
  const id = useId()

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  return (
    <svg
      viewBox="0 0 120 100"
      width={180}
      height={150}
      style={containerStyle}
      className={className}
      onClick={disabled ? undefined : onClick}
    >
      <defs>
        {/* Ceramic mortar gradient */}
        <linearGradient id={`mortar-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#D8D0C8" />
          <stop offset="15%" stopColor="#F0E8E0" />
          <stop offset="50%" stopColor="#FFFFFF" />
          <stop offset="85%" stopColor="#F0E8E0" />
          <stop offset="100%" stopColor="#D8D0C8" />
        </linearGradient>

        {/* Mortar interior gradient */}
        <radialGradient id={`mortar-inner-${id}`} cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#E8E0D8" />
          <stop offset="70%" stopColor="#D0C8C0" />
          <stop offset="100%" stopColor="#B8B0A8" />
        </radialGradient>

        {/* Pestle gradient */}
        <linearGradient id={`pestle-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#C8C0B8" />
          <stop offset="20%" stopColor="#E8E0D8" />
          <stop offset="50%" stopColor="#F8F0E8" />
          <stop offset="80%" stopColor="#E8E0D8" />
          <stop offset="100%" stopColor="#C8C0B8" />
        </linearGradient>

        {/* Pestle tip gradient (darker for grinding surface) */}
        <radialGradient id={`pestle-tip-${id}`} cx="50%" cy="80%" r="60%">
          <stop offset="0%" stopColor="#D0C8C0" />
          <stop offset="100%" stopColor="#A8A098" />
        </radialGradient>

        {/* Content gradient */}
        <linearGradient id={`content-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={contentColor} stopOpacity="0.9" />
          <stop offset="100%" stopColor={contentColor} stopOpacity="1" />
        </linearGradient>

        {/* Shadow */}
        <filter id={`shadow-${id}`} x="-15%" y="-10%" width="130%" height="130%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.25"/>
        </filter>

        {/* Texture pattern for rough ceramic */}
        <pattern id={`texture-${id}`} patternUnits="userSpaceOnUse" width="4" height="4">
          <circle cx="1" cy="1" r="0.5" fill="rgba(0,0,0,0.03)" />
          <circle cx="3" cy="3" r="0.3" fill="rgba(0,0,0,0.02)" />
        </pattern>

        {/* Powder particle pattern */}
        <pattern id={`powder-${id}`} patternUnits="userSpaceOnUse" width="8" height="8">
          <circle cx="2" cy="2" r="1" fill="rgba(0,0,0,0.1)" />
          <circle cx="6" cy="5" r="0.8" fill="rgba(0,0,0,0.08)" />
          <circle cx="4" cy="7" r="0.6" fill="rgba(0,0,0,0.06)" />
        </pattern>
      </defs>

      <g filter={`url(#shadow-${id})`}>
        {/* Mortar base (bowl) */}
        <path
          d={`
            M20 45
            Q20 90 60 90
            Q100 90 100 45
            L100 50
            Q100 95 60 95
            Q20 95 20 50
            Z
          `}
          fill={`url(#mortar-${id})`}
          stroke="#B0A8A0"
          strokeWidth="1.5"
        />

        {/* Mortar rim */}
        <ellipse
          cx="60"
          cy="45"
          rx="40"
          ry="12"
          fill={`url(#mortar-${id})`}
          stroke="#B0A8A0"
          strokeWidth="1.5"
        />

        {/* Mortar inner surface */}
        <ellipse
          cx="60"
          cy="47"
          rx="35"
          ry="10"
          fill={`url(#mortar-inner-${id})`}
        />

        {/* Texture overlay */}
        <ellipse
          cx="60"
          cy="47"
          rx="35"
          ry="10"
          fill={`url(#texture-${id})`}
        />

        {/* Inner shadow */}
        <ellipse
          cx="60"
          cy="50"
          rx="30"
          ry="8"
          fill="rgba(0,0,0,0.1)"
        />

        {/* Content (powder/tablets) */}
        {hasContent && (
          <g>
            {/* Content base */}
            <ellipse
              cx="60"
              cy={55 + (100 - contentLevel) * 0.15}
              rx={28 - (100 - contentLevel) * 0.1}
              ry={6 - (100 - contentLevel) * 0.02}
              fill={`url(#content-${id})`}
            />
            {/* Powder texture */}
            <ellipse
              cx="60"
              cy={55 + (100 - contentLevel) * 0.15}
              rx={28 - (100 - contentLevel) * 0.1}
              ry={6 - (100 - contentLevel) * 0.02}
              fill={`url(#powder-${id})`}
            />
            {/* Content highlights */}
            <ellipse
              cx="55"
              cy={53 + (100 - contentLevel) * 0.15}
              rx="8"
              ry="2"
              fill="rgba(255,255,255,0.3)"
            />
          </g>
        )}

        {/* Mortar rim highlight */}
        <path
          d={`M25 43 Q60 32 95 43`}
          fill="none"
          stroke="rgba(255,255,255,0.6)"
          strokeWidth="2"
          strokeLinecap="round"
        />

        {/* Pestle */}
        <g
          transform={grinding ? '' : 'translate(0, 0)'}
          style={grinding ? { animation: 'grind 0.5s ease-in-out infinite' } : {}}
        >
          {/* Pestle handle (top) */}
          <path
            d={`
              M85 10
              Q90 10 92 15
              L92 30
              Q92 35 88 38
              L82 38
              Q78 35 78 30
              L78 15
              Q80 10 85 10
            `}
            fill={`url(#pestle-${id})`}
            stroke="#A8A098"
            strokeWidth="1"
          />

          {/* Pestle shaft */}
          <rect
            x="80"
            y="38"
            width="10"
            height="25"
            fill={`url(#pestle-${id})`}
            stroke="#A8A098"
            strokeWidth="1"
          />

          {/* Pestle grinding end */}
          <ellipse
            cx="85"
            cy="65"
            rx="8"
            ry="12"
            fill={`url(#pestle-tip-${id})`}
            stroke="#A8A098"
            strokeWidth="1"
          />

          {/* Pestle tip bottom */}
          <ellipse
            cx="85"
            cy="72"
            rx="5"
            ry="4"
            fill="#B8B0A8"
          />

          {/* Pestle shaft highlight */}
          <line
            x1="81"
            y1="15"
            x2="81"
            y2="60"
            stroke="rgba(255,255,255,0.4)"
            strokeWidth="1.5"
            strokeLinecap="round"
          />

          {/* Pestle handle highlight */}
          <path
            d={`M82 12 Q85 8 88 12`}
            fill="none"
            stroke="rgba(255,255,255,0.5)"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </g>

        {/* Grinding particles when grinding */}
        {grinding && hasContent && (
          <g>
            {[0, 1, 2, 3, 4].map((i) => (
              <circle
                key={i}
                cx={55 + i * 8}
                cy="55"
                r="1.5"
                fill={contentColor}
              >
                <animate
                  attributeName="cy"
                  values="55;50;55"
                  dur={`${0.3 + i * 0.1}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="1;0.3;1"
                  dur={`${0.3 + i * 0.1}s`}
                  repeatCount="indefinite"
                />
              </circle>
            ))}
          </g>
        )}

        {/* Mortar base shadow */}
        <ellipse
          cx="60"
          cy="96"
          rx="35"
          ry="4"
          fill="rgba(0,0,0,0.15)"
        />
      </g>

      {/* Grinding animation keyframes */}
      <style>{`
        @keyframes grind {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(-3px, 2px) rotate(-5deg); }
          50% { transform: translate(0, 3px) rotate(0deg); }
          75% { transform: translate(3px, 2px) rotate(5deg); }
        }
      `}</style>
    </svg>
  )
}

export default MortarAndPestle
